import { useState } from 'react';

import styled from '@emotion/styled';

import StatusCell from '../../../components/Badge/StatusCell';
import { PrimaryBlackButton, PrimaryWhiteButton } from '../../../components/Button/Buttons';
import TableSliderContentWrapper from '../../../components/Table/TableAccessories/TableSliderContentWrapper';
import useRiskAlert from '../../../hooks/dashboard/risk/useRiskAlert';
import {
  ALERT_ACTIONS,
  ReviewAction,
  RiskAlert,
} from '../../../models/risk/risk.model';
import { capitalize, formatNaira, tableFormatDateTimeNG } from '../../../utils/utils';
import { TransactionDetailBody } from '../Transactions/TransactionDetailSlider';
import { CustomerProfileBody } from './CustomerProfileSlider';

const ACTION_LABELS: Record<ReviewAction, string> = {
  acknowledge: 'Acknowledge',
  resolve: 'Resolve',
  dismiss: 'False positive',
  reopen: 'Reopen',
};

// Resolving or dismissing records why; the backend rejects these without a note.
const NOTE_REQUIRED: ReviewAction[] = ['resolve', 'dismiss'];

const formatDate = (value?: string | null) => (value ? tableFormatDateTimeNG(value) : '—');

interface AlertDetailSliderProps {
  data: RiskAlert;
  // Called after a review so the list behind the slider can refresh.
  onChanged?: () => void;
}

const AlertDetailSlider: React.FC<AlertDetailSliderProps> = ({ data, onChanged }) => {
  const { alert, loading, saving, review } = useRiskAlert(data.id);
  const [note, setNote] = useState('');
  const current = alert ?? data;
  const showTransaction =
    !!current.transaction_reference &&
    (current.entity_type === 'TRANSACTION' || current.entity_type === 'LEDGER');

  const act = (action: ReviewAction) =>
    review(action, note, () => {
      setNote('');
      onChanged?.();
    });

  return (
    <TableSliderContentWrapper label='Alert'>
      <Section>
        <PillRow>
          <StatusCell data={current} statusKey='severity' />
          <StatusCell data={current} statusKey='status' />
        </PillRow>
        <Title>{current.title}</Title>
        <Detail>{current.detail}</Detail>
        {Number(current.amount_at_risk) > 0 && (
          <Row label='Amount at risk'>{formatNaira(Number(current.amount_at_risk))}</Row>
        )}
        <Row label='Customer'>{current.chat_id ?? '—'}</Row>
        <Row label='Began'>{formatDate(current.first_detected_at)}</Row>
        <Row label='Last seen by scan'>{formatDate(current.last_detected_at)}</Row>
        {Object.entries(current.evidence ?? {}).map(([key, value]) =>
          value === null || value === undefined ? null : (
            <Row key={key} label={capitalize(key.replace(/_/g, ' '))}>
              {Array.isArray(value) ? `${value.length} item(s)` : String(value)}
            </Row>
          )
        )}
      </Section>

      <Section>
        <SectionTitle>Review</SectionTitle>
        {current.reviewed_by && (
          <Muted>
            Last reviewed by {current.reviewed_by} · {formatDate(current.reviewed_at)}
            {current.review_note ? ` — “${current.review_note}”` : ''}
          </Muted>
        )}
        <NoteInput
          placeholder='What did you find? (required to resolve or mark false positive)'
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={2000}
        />
        <ActionRow>
          {ALERT_ACTIONS[current.status].map((action) => {
            const Button = action === 'resolve' ? PrimaryBlackButton : PrimaryWhiteButton;
            const needsNote = NOTE_REQUIRED.includes(action) && !note.trim();
            return (
              <Button
                key={action}
                type='button'
                className='large'
                disabled={saving || needsNote}
                title={needsNote ? 'Add a note first' : undefined}
                onClick={() => act(action)}
              >
                {ACTION_LABELS[action]}
              </Button>
            );
          })}
        </ActionRow>
      </Section>

      {!loading && alert && alert.history.length > 0 && (
        <Section>
          <SectionTitle>History</SectionTitle>
          {alert.history.map((event, index) => (
            <HistoryRow key={index}>
              <span>
                <strong>{capitalize(event.event_type.replace(/_/g, ' '))}</strong>
                {event.new_status && event.previous_status !== event.new_status
                  ? ` → ${capitalize(event.new_status)}`
                  : ''}
                {event.note && <Muted>{event.note}</Muted>}
              </span>
              <Muted>
                {event.actor ?? 'system'}
                <br />
                {formatDate(event.created_at)}
              </Muted>
            </HistoryRow>
          ))}
        </Section>
      )}

      {showTransaction ? (
        <>
          <SectionTitle>Transaction</SectionTitle>
          <TransactionDetailBody reference={current.transaction_reference!} />
        </>
      ) : (
        current.user_id && (
          <>
            <SectionTitle>Customer 360</SectionTitle>
            <CustomerProfileBody userId={current.user_id} />
          </>
        )
      )}
    </TableSliderContentWrapper>
  );
};

export default AlertDetailSlider;

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <DetailRow>
    <DetailLabel>{label}</DetailLabel>
    <DetailValue>{children}</DetailValue>
  </DetailRow>
);

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 20px;
  border-bottom: 1px solid #E2DED3;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #5D665F;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const PillRow = styled.div`
  display: flex;
  gap: 8px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #0E1A13;
`;

const Detail = styled.div`
  font-size: 14px;
  color: #3F4A42;
`;

const Muted = styled.div`
  font-size: 12px;
  color: #5D665F;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
`;

const DetailLabel = styled.span`
  color: #5D665F;
`;

const DetailValue = styled.span`
  color: #0E1A13;
  font-weight: 500;
  text-align: right;
  word-break: break-all;
`;

const NoteInput = styled.textarea`
  border: 1px solid #E2DED3;
  border-radius: 6px;
  padding: 10px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  & > button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HistoryRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: #0E1A13;

  & > div:last-of-type {
    text-align: right;
    flex-shrink: 0;
  }
`;
